import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";

import UsersTable from "../components/admin/UsersTable";
import IncomingTasks from "../components/admin/IncomingTasks";

const API = import.meta.env.VITE_API_URL || "https://api.runwaycrew.com";

// Types
type Brand = { id: string; name: string };
type User = { id: string; email: string; name?: string; role: string };
type BrandTarget = {
  kpi: string;
  target_value: number | null;
  effective_week_start: string; // ISO date
  unit?: "" | "$" | "%";
};

type NorthStar = {
  id: string;
  brand_id: string;
  goal: string;
  target_value?: number | null;
  metric_type?: string | null;
  deadline?: string | null;
  status: "active" | "completed" | "abandoned" | "archived";
  completed_at?: string | null;
  created_at: string;
  updated_at?: string;
  notes?: string | null;
};

type Task = {
  id: string;
  brand_id: string;
  title: string;
  description?: string;
  status: string;
  kpi?: string | null;
  due_date?: string | null;
  created_at: string;
  // new, optional fields used by admin views
  created_by_role?: "admin" | "brand";
  created_by_name?: string | null;
  brand_name?: string | null;
  approved?: boolean | null;
  approval_note?: string | null;
  priority?: "low" | "normal" | "high" | "urgent" | string;
  requested_due_date?: string | null;
};

const KPI_CATALOG: Array<{ key: string; label: string; unit: "$" | "%" | "count" }> = [
  { key: "revenue_usd", label: "Net Sales ($)", unit: "$" },
  { key: "conversion_pct", label: "Conversion Rate (%)", unit: "%" },
  { key: "contribution_per_customer_usd", label: "CpC (Contrib / Customer) ($)", unit: "$" },
  { key: "contribution_per_order_usd", label: "CpO ($)", unit: "$" },
  { key: "ltv_contribution_usd", label: "Customer LTV ($)", unit: "$" },
  { key: "active_subs", label: "Active Subs (count)", unit: "count" },
  { key: "churn_pct", label: "Churn (%)", unit: "%" },
  { key: "subscriber_ltv_usd", label: "Subscriber LTV ($)", unit: "$" },
];

type Lead = {
  id: string;
  lead_date: string;           // ISO (yyyy-mm-dd)
  contact_name: string;
  company?: string | null;
  email?: string | null;
  medium: 'email' | 'linkedin' | 'twitter' | 'intro' | 'phone' | 'event' | 'other';
  context?: string | null;
  status: 'new' | 'contacted' | 'meeting' | 'proposal' | 'won' | 'lost';
  next_action?: string | null;
  next_action_date?: string | null;
  owner_user_id?: string | null;
  created_at: string;
  updated_at: string;
};

const LEAD_MEDIUMS: Array<{value: Lead["medium"]; label: string}> = [
  { value: "email", label: "Email" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "twitter", label: "Twitter/X" },
  { value: "intro", label: "Intro" },
  { value: "phone", label: "Phone" },
  { value: "event", label: "Event" },
  { value: "other", label: "Other" },
];

const LEAD_STATUSES: Array<{value: Lead["status"]; label: string}> = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "meeting", label: "Meeting" },
  { value: "proposal", label: "Proposal" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];


function findKpiMeta(key: string) {
  return KPI_CATALOG.find(k => k.key === key);
}

// Add this near the top with other helper functions
function formatNumber(value: any, decimals: number = 2): string {
  const num = Number(value);
  return isNaN(num) ? '0.00' : num.toFixed(decimals);
}

function safeDate(d: any) {
  if (!d) return "";
  try {
    // If the string STARTS with YYYY-MM-DD, trust those 10 chars literally
    if (typeof d === "string") {
      const m = d.match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) {
        const [y, mo, da] = m[1].split("-");
        return `${+mo}/${+da}/${y}`;
      }
    }
    // Fallback: format using UTC pieces to avoid local TZ shifts
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return String(d ?? "");
    return `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}/${dt.getUTCFullYear()}`;
  } catch {
    return String(d ?? "");
  }
}

function pct(n: number, d: number) {
  if (!d) return 0;
  return (n / d) * 100;
}

function FunnelByBrand({
  brands,
  selectedBrandId,
  onBrandChange,
}: {
  brands: Brand[];
  selectedBrandId: string;
  onBrandChange: (id: string) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const sevenDaysAgo = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  // inclusive UI range (7 days including today)
  const [fromDate, setFromDate] = useState<string>(sevenDaysAgo);
  const [toDate, setToDate] = useState<string>(today);


  // Event-based funnel (NEW endpoint)
  const [funnel, setFunnel] = useState<any>(null);
  const [loadingFunnel, setLoadingFunnel] = useState(false);

  // Weekly history (snapshots)
  const [rows, setRows] = useState<any[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);

  async function loadAll() {
    if (!selectedBrandId) return;

    setLoadingFunnel(true);
    setLoadingRows(true);

    try {
      // 1) NEW event-based funnel endpoint
      const qs = new URLSearchParams();
      if (fromDate) qs.set("from", fromDate); // YYYY-MM-DD
      if (toDate) {
        // backend uses exclusive upper bound; add 1 day so UI "to" is inclusive
        const toExclusive = new Date(toDate + "T00:00:00Z");
        toExclusive.setUTCDate(toExclusive.getUTCDate() + 1);
        qs.set("to", toExclusive.toISOString());
      }

      const fRes = await fetch(
        `${API}/admin/brands/${selectedBrandId}/funnel?${qs.toString()}`,
        { credentials: "include" }
      );

      const fJson = await fRes.json().catch(() => null);
      setFunnel(fJson?.funnel || null);

      // 2) Weekly snapshots (your existing endpoint)
      const r = await fetch(`${API}/brands/${selectedBrandId}/metrics`, { credentials: "include" });
      const j = await r.json().catch(() => []);
      setRows(Array.isArray(j) ? j : []);
    } finally {
      setLoadingFunnel(false);
      setLoadingRows(false);
    }
  }

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrandId, fromDate, toDate]);

  // Event funnel fields (NEW endpoint)
  const sessions = Number(funnel?.sessions || 0);
  const atc = Number(funnel?.add_to_cart_sessions || 0);
  const checkout = Number(funnel?.checkout_sessions || 0);
  const orders = Number(funnel?.order_sessions || 0);

  // Percentages already computed by API (use those instead of recomputing)
  const atcRate = Number(funnel?.add_to_cart_rate_pct || 0);
  const checkoutRate = Number(funnel?.checkout_rate_pct || 0);
  const orderRate = Number(funnel?.order_rate_pct || 0);
  const checkoutToOrder = Number(funnel?.checkout_to_order_pct || 0);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="min-w-[220px]">
          <Select
            label="Brand"
            name="funnel_brand_id"
            value={selectedBrandId}
            onChange={(e) => onBrandChange(e.target.value)}
          >
            {brands.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </Select>
        </div>

        <div className="flex items-end gap-2 flex-wrap">
  <div>
    <label className="block text-xs text-gray-500 mb-1">From</label>
    <input
      type="date"
      className="px-3 py-2 border rounded-lg text-sm"
      value={fromDate}
      onChange={(e) => setFromDate(e.target.value)}
    />
  </div>

  <div>
        <label className="block text-xs text-gray-500 mb-1">To</label>
        <input
          type="date"
          className="px-3 py-2 border rounded-lg text-sm"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />
      </div>

      <button
        onClick={loadAll}
        className="text-sm px-3 py-2 border rounded-lg hover:bg-gray-50 h-[42px]"
      >
        Refresh
      </button>

      <button
        type="button"
        onClick={() => {
          // quick reset to last 7 days
          setFromDate(sevenDaysAgo);
          setToDate(today);
        }}
        className="text-sm px-3 py-2 border rounded-lg hover:bg-gray-50 h-[42px]"
      >
        Last 7 days
      </button>
    </div>

      </div>

      {/* Summary cards (EVENT-BASED) */}
      <div className="grid md:grid-cols-5 gap-3">
        <div className="bg-white border rounded-lg p-4">
          <div className="text-xs text-gray-500">Sessions</div>
          <div className="text-xl font-semibold">{sessions.toLocaleString()}</div>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="text-xs text-gray-500">Add to Cart</div>
          <div className="text-xl font-semibold">{atc.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">{atcRate.toFixed(2)}% of sessions</div>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="text-xs text-gray-500">Checkout</div>
          <div className="text-xl font-semibold">{checkout.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">{checkoutRate.toFixed(2)}% of sessions</div>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="text-xs text-gray-500">Orders</div>
          <div className="text-xl font-semibold">{orders.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">{checkoutToOrder.toFixed(2)}% of checkout</div>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="text-xs text-gray-500">CVR</div>
          <div className="text-xl font-semibold">{orderRate.toFixed(2)}%</div>
          <div className="text-xs text-gray-500 mt-1">Orders / Sessions</div>
        </div>
      </div>

      {(loadingFunnel || loadingRows) && (
        <div className="text-gray-500 text-sm">Loading…</div>
      )}

      {/* History table (weekly snapshots) */}
      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left py-2 px-3">Week</th>
              <th className="text-right py-2 px-3">Sessions</th>
              <th className="text-right py-2 px-3">ATC</th>
              <th className="text-right py-2 px-3">Checkout</th>
              <th className="text-right py-2 px-3">Orders</th>
              <th className="text-right py-2 px-3">CVR</th>
              <th className="text-right py-2 px-3">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {!loadingRows && rows.map((r) => {
              const s = Number(r.sessions || 0);
              const a = Number(r.atc || 0);
              const c = Number(r.checkout || 0);
              const o = Number(r.orders || 0);
              const rev = Number(r.revenue_usd || 0);

              return (
                <tr key={r.week_start_date} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-3">{safeDate(r.week_start_date)}</td>
                  <td className="py-2 px-3 text-right">{s.toLocaleString()}</td>
                  <td className="py-2 px-3 text-right">{a.toLocaleString()}</td>
                  <td className="py-2 px-3 text-right">{c.toLocaleString()}</td>
                  <td className="py-2 px-3 text-right font-medium">{o.toLocaleString()}</td>
                  <td className="py-2 px-3 text-right">{pct(o, s).toFixed(2)}%</td>
                  <td className="py-2 px-3 text-right">${rev.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                </tr>
              );
            })}
            {!loadingRows && !rows.length && (
              <tr><td className="py-6 text-center text-gray-500" colSpan={7}>No weekly funnel data yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-500">
        Top cards = event-based funnel ({fromDate} → {toDate}). Table = weekly snapshots.
      </p>
    </div>
  );
}




async function fetchCurrentValues(brandId: string) {
  const [orders, subs] = await Promise.all([
    fetch(`${API}/brands/${brandId}/metrics`, { credentials: "include" }).then(r => r.json()).catch(() => []),
    fetch(`${API}/brands/${brandId}/subs-metrics`, { credentials: "include" }).then(r => r.json()).catch(() => []),
  ]);
  const latestOrders = Array.isArray(orders) ? orders[0] : null;
  const latestSubs = Array.isArray(subs) ? subs[0] : null;
  return { latestOrders, latestSubs };
}

function currentForKpi(kpiKey: string, latestOrders: any, latestSubs: any): number | null {
  const mapOrders: Record<string,string> = {
    revenue_usd: "revenue_usd",
    conversion_pct: "conversion_pct",
    contribution_per_customer_usd: "contribution_per_customer_usd",
    ltv_contribution_usd: "ltv_contribution_usd",
  };
  const mapSubs: Record<string,string> = {
    active_subs: "end_subs",
    churn_pct: "churn_pct",
    subscriber_ltv_usd: "subscriber_ltv_usd",
    contribution_per_order_usd: "contribution_per_order_usd",
  };

  if (mapOrders[kpiKey] && latestOrders) return Number(latestOrders[mapOrders[kpiKey]]) ?? null;
  if (mapSubs[kpiKey] && latestSubs) return Number(latestSubs[mapSubs[kpiKey]]) ?? null;
  return null;
}

function LeadForm({ onSaved, users }: { onSaved?: () => void; users: User[] }) {
  const [leadDate, setLeadDate] = useState<string>(new Date().toISOString().slice(0,10));
  const [contactName, setContactName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [medium, setMedium] = useState<Lead["medium"]>("email");
  const [status, setStatus] = useState<Lead["status"]>("new");
  const [context, setContext] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [nextActionDate, setNextActionDate] = useState("");
  const [owner, setOwner] = useState("");

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const body = {
      lead_date: leadDate,
      contact_name: contactName.trim(),
      company: company.trim() || null,
      email: email.trim() || null,
      medium,
      status,
      context: context.trim() || null,
      next_action: nextAction.trim() || null,
      next_action_date: nextActionDate || null,
      owner_user_id: owner || null,
    };
    const res = await fetch(`${API}/admin/leads`, {
      method: "POST",
      headers: {"Content-Type":"application/json"},
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (res.ok) {
      setContactName(""); setCompany(""); setEmail(""); setContext(""); setNextAction(""); setNextActionDate("");
      setStatus("new"); setMedium("email");
      onSaved?.();
    } else {
      alert("Failed to save lead");
    }
  }

  return (
    <form onSubmit={save} className="space-y-4">
      <div className="grid md:grid-cols-3 gap-4">
        <Input label="Date" name="lead_date" type="date" value={leadDate} onChange={(e)=>setLeadDate(e.target.value)} required />
        <Input label="Contact" name="contact_name" value={contactName} onChange={(e)=>setContactName(e.target.value)} required />
        <Input label="Company (optional)" name="company" value={company} onChange={(e)=>setCompany(e.target.value)} />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <Input label="Email (optional)" name="email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} />
        <Select label="Medium" name="medium" value={medium} onChange={(e)=>setMedium(e.target.value as Lead["medium"])}>
          {LEAD_MEDIUMS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </Select>
        <Select label="Status" name="status" value={status} onChange={(e)=>setStatus(e.target.value as Lead["status"])}>
          {LEAD_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <Input label="Next Action (optional)" name="next_action" value={nextAction} onChange={(e)=>setNextAction(e.target.value)} />
        <Input label="Next Action Date (optional)" name="next_action_date" type="date" value={nextActionDate} onChange={(e)=>setNextActionDate(e.target.value)} />
        <Select label="Owner (optional)" name="owner_user_id" value={owner} onChange={(e)=>setOwner(e.target.value)}>
          <option value="">Unassigned</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.email}{u.name ? ` (${u.name})` : ""}</option>)}
        </Select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Context / Notes</label>
        <textarea
          rows={3}
          value={context}
          onChange={(e)=>setContext(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Where did they come from? What do they care about? Any ask?"
        />
      </div>
      <Button type="submit">Add Lead</Button>
    </form>
  );
}

function MobileLeadsCards({ refreshKey = 0 }: { refreshKey?: number }) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [mediumFilter, setMediumFilter] = useState<string>("");

  async function load() {
    const qs = new URLSearchParams();
    if (statusFilter) qs.set("status", statusFilter);
    if (mediumFilter) qs.set("medium", mediumFilter);
    if (q) qs.set("q", q);
    const r = await fetch(`${API}/admin/leads?${qs.toString()}`, { credentials: "include" });
    const j = await r.json().catch(()=>[]);
    setLeads(Array.isArray(j) ? j : []);
  }

  useEffect(() => { void load(); }, [refreshKey]);

  async function patch(id: string, patch: Partial<Lead>) {
    const prev = leads;
    setLeads(prev => prev.map(l => l.id === id ? { ...l, ...patch } : l));
    const res = await fetch(`${API}/admin/leads/${id}`, {
      method: "PATCH",
      headers: {"Content-Type":"application/json"},
      credentials: "include",
      body: JSON.stringify(patch),
    });
    if (!res.ok) { setLeads(prev); alert("Failed to update lead"); }
  }

  async function remove(id: string) {
    if (!confirm("Delete lead?")) return;
    const prev = leads;
    setLeads(prev => prev.filter(l => l.id !== id));
    const res = await fetch(`${API}/admin/leads/${id}`, { method: "DELETE", credentials: "include" });
    if (!res.ok) { setLeads(prev); alert("Failed to delete lead"); }
  }

  return (
    <div className="space-y-3 md:hidden">
      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <input
          className="flex-1 min-w-[140px] px-3 py-2 border rounded-lg text-sm"
          placeholder="Search…"
          value={q}
          onChange={(e)=>setQ(e.target.value)}
        />
        <select className="px-3 py-2 border rounded-lg text-sm" value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {LEAD_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select className="px-3 py-2 border rounded-lg text-sm" value={mediumFilter} onChange={(e)=>setMediumFilter(e.target.value)}>
          <option value="">All mediums</option>
          {LEAD_MEDIUMS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <button onClick={load} className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-50">Filter</button>
      </div>

      {/* Cards */}
      {!leads.length && (
        <div className="py-6 text-center text-gray-500">No leads yet</div>
      )}
      {leads.map((l) => (
        <div key={l.id} className="border rounded-lg p-4 bg-white">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm text-gray-500">{safeDate(l.lead_date)}</div>
              <div className="font-medium text-gray-900 break-words">{l.contact_name}</div>
              {l.company && <div className="text-sm text-gray-600 break-words">{l.company}</div>}
              {l.email && (
                <div className="text-sm text-gray-600 break-all">{l.email}</div>
              )}
            </div>
            <button
              onClick={() => remove(l.id)}
              className="text-xs px-2 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50"
            >
              Delete
            </button>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select
              className="text-xs h-8 px-2 border rounded"
              value={l.medium}
              onChange={(e)=>patch(l.id, { medium: e.target.value as Lead["medium"] })}
            >
              {LEAD_MEDIUMS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <select
              className="text-xs h-8 px-2 border rounded"
              value={l.status}
              onChange={(e)=>patch(l.id, { status: e.target.value as Lead["status"] })}
            >
              {LEAD_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <input
              className="text-xs h-8 px-2 border rounded flex-1 min-w-[140px]"
              placeholder="Next action"
              value={l.next_action || ""}
              onChange={(e)=>patch(l.id, { next_action: e.target.value })}
            />
            <input
              type="date"
              className="text-xs h-8 px-2 border rounded"
              value={toDateInputValue(l.next_action_date)}
              onChange={(e)=>patch(l.id, { next_action_date: e.target.value || null })}
            />
          </div>

          <div className="mt-3">
            <label className="block text-xs text-gray-500 mb-1">Context / Notes</label>
            <textarea
              rows={3}
              className="w-full text-sm border rounded px-2 py-1"
              value={l.context || ""}
              onChange={(e)=>patch(l.id, { context: e.target.value })}
              placeholder="Short notes…"
            />
          </div>
        </div>
      ))}
    </div>
  );
}


function LeadsTable({ refreshKey = 0 }: { refreshKey?: number }) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [mediumFilter, setMediumFilter] = useState<string>("");
  const [q, setQ] = useState("");

  async function load() {
    const qs = new URLSearchParams();
    if (statusFilter) qs.set("status", statusFilter);
    if (mediumFilter) qs.set("medium", mediumFilter);
    if (q) qs.set("q", q);
    const r = await fetch(`${API}/admin/leads?${qs.toString()}`, { credentials: "include" });
    const j = await r.json().catch(()=>[]);
    setLeads(Array.isArray(j) ? j : []);
  }

  useEffect(() => { void load(); }, [refreshKey]);

  async function patch(id: string, patch: Partial<Lead>) {
    const prev = leads;
    setLeads(prev => prev.map(l => l.id === id ? { ...l, ...patch } : l));
    const res = await fetch(`${API}/admin/leads/${id}`, {
      method: "PATCH",
      headers: {"Content-Type":"application/json"},
      credentials: "include",
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      setLeads(prev); // rollback
      alert("Failed to update lead");
    }
  }

  async function remove(id: string) {
    if (!confirm("Delete lead?")) return;
    const prev = leads;
    setLeads(prev => prev.filter(l => l.id !== id));
    const res = await fetch(`${API}/admin/leads/${id}`, { method: "DELETE", credentials: "include" });
    if (!res.ok) { setLeads(prev); alert("Failed to delete lead"); }
  }

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-4">
        <input
          className="px-3 py-2 border rounded-lg text-sm"
          placeholder="Search name/company/context…"
          value={q}
          onChange={(e)=>setQ(e.target.value)}
        />
        <select className="px-3 py-2 border rounded-lg text-sm" value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {LEAD_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select className="px-3 py-2 border rounded-lg text-sm" value={mediumFilter} onChange={(e)=>setMediumFilter(e.target.value)}>
          <option value="">All mediums</option>
          {LEAD_MEDIUMS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <button onClick={load} className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-50">Filter</button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left py-2 px-2">Date</th>
              <th className="text-left py-2 px-2">Contact</th>
              <th className="text-left py-2 px-2">Company</th>
              <th className="text-left py-2 px-2">Email</th>
              <th className="text-left py-2 px-2">Medium</th>
              <th className="text-left py-2 px-2">Status</th>
              <th className="text-left py-2 px-2">Context</th>
              <th className="text-left py-2 px-2">Next</th>
              <th className="text-right py-2 px-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.map(l => (
              <tr key={l.id} className="border-b hover:bg-gray-50">
                <td className="py-2 px-2">{safeDate(l.lead_date)}</td>
                <td className="py-2 px-2">{l.contact_name}</td>
                <td className="py-2 px-2">{l.company || "—"}</td>
                <td className="py-2 px-2 break-all">{l.email || "—"}</td>
                <td className="py-2 px-2">
                  <select
                    className="border rounded px-1 py-1 text-xs"
                    value={l.medium}
                    onChange={(e)=>patch(l.id, { medium: e.target.value as Lead["medium"] })}
                  >
                    {LEAD_MEDIUMS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </td>
                <td className="py-2 px-2">
                  <select
                    className="border rounded px-1 py-1 text-xs"
                    value={l.status}
                    onChange={(e)=>patch(l.id, { status: e.target.value as Lead["status"] })}
                  >
                    {LEAD_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </td>
                <td className="py-2 px-2 max-w-[360px]">
                  <textarea
                    className="w-full border rounded px-2 py-1 text-xs"
                    rows={2}
                    value={l.context || ""}
                    onChange={(e)=>patch(l.id, { context: e.target.value })}
                    placeholder="Short notes…"
                  />
                </td>
                <td className="py-2 px-2">
                  <div className="flex flex-col gap-1">
                    <input
                      className="border rounded px-2 py-1 text-xs"
                      placeholder="Next action"
                      value={l.next_action || ""}
                      onChange={(e)=>patch(l.id, { next_action: e.target.value })}
                    />
                    <input
                      type="date"
                      className="border rounded px-2 py-1 text-xs"
                      value={toDateInputValue(l.next_action_date)}
                      onChange={(e)=>patch(l.id, { next_action_date: e.target.value || null })}
                    />
                  </div>
                </td>
                <td className="py-2 px-2 text-right">
                  <button onClick={()=>remove(l.id)} className="text-xs px-3 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50">Delete</button>
                </td>
              </tr>
            ))}
            {!leads.length && (
              <tr><td className="py-6 text-center text-gray-500" colSpan={9}>No leads yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** Inline, self-contained login form */
function LoginForm({ redirectTo = "/dashboard" }: { redirectTo?: string }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || "Invalid email or password");
      }
      window.location.assign(redirectTo || "/dashboard");
    } catch (err: any) {
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="bg-white p-8 rounded-xl border shadow-lg w-full max-w-md space-y-4">
      <h2 className="text-2xl font-bold text-center">Welcome Back</h2>
      {msg && <div className="text-red-600 text-sm bg-red-50 p-3 rounded">{msg}</div>}
      <div>
        <label className="block text-sm font-medium mb-2">Email</label>
        <input 
          type="email" 
          className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors" 
          value={email} 
          onChange={e=>setEmail(e.target.value)} 
          required 
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Password</label>
        <input 
          type="password" 
          className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors" 
          value={password} 
          onChange={e=>setPassword(e.target.value)} 
          required 
        />
      </div>
      <button 
        className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors" 
        disabled={busy}
      >
        {busy ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
}

// Reusable Components
function Card({ title, children, right }: { title: string; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {right}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function Input({
  label,
  name,
  type = "text",
  placeholder,
  required,
  defaultValue,
  value,
  onChange,
  step,
}: {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
  defaultValue?: string;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  step?: string;
}) {
  return (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        placeholder={placeholder}
        required={required}
        defaultValue={defaultValue}
        value={value}
        onChange={onChange}
        step={step}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
      />
    </div>
  );
}

function Select({
  label,
  name,
  children,
  value,
  onChange,
}: {
  label: string;
  name: string;
  children: React.ReactNode;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}) {
  return (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
      >
        {children}
      </select>
    </div>
  );
}

function Button({ type = "button", children, onClick, className = "" }: { type?: "button" | "submit"; children: React.ReactNode; onClick?: () => void; className?: string }) {
  return (
    <button
      type={type}
      onClick={onClick}
      className={`w-full px-4 py-2 rounded-lg font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700 ${className}`}
    >
      {children}
    </button>
  );
}

/* ---------------------- Edit Modals (unchanged) ---------------------- */
// ... (EditWeeklyOrderModal and EditWeeklySubsModal are identical to your version above) ...

// Edit Modal for Weekly Orders
function EditWeeklyOrderModal({ 
  brandId, 
  weekStart, 
  record, 
  onClose, 
  onSaved 
}: { 
  brandId: string; 
  weekStart: string; 
  record: any; 
  onClose: () => void; 
  onSaved: () => void;
}) {
  const [formData, setFormData] = useState({
    sessions: record?.sessions || 0,
    atc: record?.atc || 0,
    checkout: record?.checkout || 0,
    orders: record?.orders || 0,
    revenue_usd: record?.revenue_usd || 0,
    ad_spend_usd: record?.ad_spend_usd || 0,
    new_customers: record?.new_customers || 0,
    repeat_customers: record?.repeat_customers || 0,
    gross_margin_pct: record?.gross_margin_pct_display || 0,
    total_shipping_charges_usd: record?.total_shipping_charges_usd || 0,
  });

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const body = {
      week_start: weekStart,
      sessions: Number(formData.sessions || 0),
      atc: Number(formData.atc || 0),
      checkout: Number(formData.checkout || 0),
      orders: Number(formData.orders || 0),
      revenue: Number(formData.revenue_usd || 0),
      ad_spend: Number(formData.ad_spend_usd || 0), 
      new_customers: Number(formData.new_customers || 0),
      repeat_customers: Number(formData.repeat_customers || 0),
      gross_margin_pct: Number(formData.gross_margin_pct || 0) / 100,
      total_shipping_charges: Number(formData.total_shipping_charges_usd || 0),
    };

    const res = await fetch(`${API}/admin/brands/${brandId}/weekly/upsert`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (res.ok) onSaved();
  }

  const updateField = (field: string, value: any) => setFormData(prev => ({ ...prev, [field]: value }));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">Edit Weekly Orders - {safeDate(weekStart)}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>
        <form onSubmit={handleSave} className="space-y-6">
          <div className="border-b pb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Funnel Metrics</h4>
            <div className="grid md:grid-cols-4 gap-4">
              <Input label="Sessions" name="sessions" type="number" value={formData.sessions} onChange={(e) => updateField('sessions', e.target.value)} />
              <Input label="Add to Cart" name="atc" type="number" value={formData.atc} onChange={(e) => updateField('atc', e.target.value)} />
              <Input label="Checkout" name="checkout" type="number" value={formData.checkout} onChange={(e) => updateField('checkout', e.target.value)} />
              <Input label="Orders" name="orders" type="number" value={formData.orders} onChange={(e) => updateField('orders', e.target.value)} />
            </div>
          </div>

          <div className="border-b pb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Revenue & Costs</h4>
            <div className="grid md:grid-cols-3 gap-4">
              <Input label="Revenue ($)" name="revenue" type="number" step="0.01" value={formData.revenue_usd} onChange={(e) => updateField('revenue_usd', e.target.value)} />
              <Input label="Ad Spend ($)" name="ad_spend" type="number" step="0.01" value={formData.ad_spend_usd} onChange={(e) => updateField('ad_spend_usd', e.target.value)} />
              <Input label="Shipping Charges ($)" name="shipping" type="number" step="0.01" value={formData.total_shipping_charges_usd} onChange={(e) => updateField('total_shipping_charges_usd', e.target.value)} />
            </div>
          </div>

          <div className="border-b pb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Customers</h4>
            <div className="grid md:grid-cols-3 gap-4">
              <Input label="New Customers" name="new_customers" type="number" value={formData.new_customers} onChange={(e) => updateField('new_customers', e.target.value)} />
              <Input label="Repeat Customers" name="repeat_customers" type="number" value={formData.repeat_customers} onChange={(e) => updateField('repeat_customers', e.target.value)} />
              <Input label="Gross Margin %" name="gross_margin" type="number" step="0.01" value={formData.gross_margin_pct} onChange={(e) => updateField('gross_margin_pct', e.target.value)} />
            </div>
          </div>
          
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">Save Changes</button>
            <button type="button" onClick={onClose} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Modal for Weekly Subscriptions
function EditWeeklySubsModal({ 
  brandId, 
  weekStart, 
  record, 
  onClose, 
  onSaved 
}: { 
  brandId: string; 
  weekStart: string; 
  record: any; 
  onClose: () => void; 
  onSaved: () => void;
}) {
  const [formData, setFormData] = useState({
    start_subs: record?.start_subs || 0,
    new_subs: record?.new_subs || 0,
    churned_subs: record?.churned_subs || 0,
    sub_aov_cents: record?.sub_aov_usd || 0,
    avg_active_days: record?.avg_active_days || 0,
    ad_spend_cents: record?.ad_spend_usd || 0,
    gross_margin_pct: record?.gross_margin_pct_display || 0, 
    fulfillment_per_order_avg_cents: record?.fulfillment_per_order_avg_usd || 0, 
  });

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const toCents = (v: number) => Math.round(v * 100);
    const body = {
      week_start_date: weekStart,
      start_subs: Number(formData.start_subs || 0),
      new_subs: Number(formData.new_subs || 0),
      churned_subs: Number(formData.churned_subs || 0),
      sub_aov_cents: toCents(Number(formData.sub_aov_cents || 0)),
      avg_active_days: Number(formData.avg_active_days || 0),
      ad_spend_cents: toCents(Number(formData.ad_spend_cents || 0)),
      gross_margin_pct: Number(formData.gross_margin_pct || 0) / 100,
      fulfillment_per_order_avg_cents: toCents(Number(formData.fulfillment_per_order_avg_cents || 0)), 
    };

    const res = await fetch(`${API}/admin/brands/${brandId}/weekly/subscriptions/upsert`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (res.ok) onSaved();
  }

  const updateField = (field: string, value: any) => setFormData(prev => ({ ...prev, [field]: value }));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">Edit Subscription Data - {safeDate(weekStart)}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>
        
        <form onSubmit={handleSave} className="space-y-6">
          <div className="border-b pb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Subscription Metrics</h4>
            <div className="grid md:grid-cols-3 gap-4">
              <Input label="Start Subs" name="start_subs" type="number" value={formData.start_subs} onChange={(e) => updateField('start_subs', e.target.value)} />
              <Input label="New Subs" name="new_subs" type="number" value={formData.new_subs} onChange={(e) => updateField('new_subs', e.target.value)} />
              <Input label="Churned Subs" name="churned_subs" type="number" value={formData.churned_subs} onChange={(e) => updateField('churned_subs', e.target.value)} />
            </div>
          </div>

          <div className="border-b pb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Economics</h4>
            <div className="grid md:grid-cols-3 gap-4">
              <Input label="Sub AOV ($)" name="sub_aov" type="number" step="0.01" value={formData.sub_aov_cents} onChange={(e) => updateField('sub_aov_cents', e.target.value)} />
              <Input label="Avg Active Days" name="avg_active_days" type="number" value={formData.avg_active_days} onChange={(e) => updateField('avg_active_days', e.target.value)} />
              <Input label="Ad Spend ($)" name="ad_spend" type="number" step="0.01" value={formData.ad_spend_cents} onChange={(e) => updateField('ad_spend_cents', e.target.value)} />
            </div>
          </div>

          <div className="border-b pb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Costs</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <Input label="Gross Margin %" name="gross_margin" type="number" step="0.01" value={formData.gross_margin_pct} onChange={(e) => updateField('gross_margin_pct', e.target.value)} />
              <Input label="Fulfillment per Order ($)" name="fulfillment" type="number" step="0.01" value={formData.fulfillment_per_order_avg_cents} onChange={(e) => updateField('fulfillment_per_order_avg_cents', e.target.value)} />
            </div>
          </div>
          
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">Save Changes</button>
            <button type="button" onClick={onClose} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordModal({
  user,
  onClose,
  onReset,
}: {
  user: User;
  onClose: () => void;
  onReset: (newPw: string) => Promise<void>;
}) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const canSave = pw.length >= 8 && pw === confirm;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    setBusy(true); setErr(null);
    try {
      await onReset(pw);
      onClose();
    } catch (e: any) {
      setErr(e?.message || "Failed to reset password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Reset Password</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        <p className="text-sm text-gray-600 mb-4">User: <strong>{user.email}</strong></p>
        {err && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <Input label="New Password" name="pw" type="password" value={pw} onChange={(e)=>setPw(e.target.value)} />
          <Input label="Confirm Password" name="confirm" type="password" value={confirm} onChange={(e)=>setConfirm(e.target.value)} />
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="px-4 py-2 rounded border" onClick={onClose} disabled={busy}>Cancel</button>
            <button
              type="submit"
              className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-60"
              disabled={!canSave || busy}
            >
              {busy ? "Saving…" : "Reset Password"}
            </button>
          </div>
          <p className="text-xs text-gray-500">Min 8 characters. This will force the user to re-authenticate if your backend checks <code>password_changed_at</code>.</p>
        </form>
      </div>
    </div>
  );
}


/* ---------------------- Weekly Lists (unchanged) ---------------------- */

function WeeklyOrdersList({ brandId, refreshKey = 0 }: { brandId: string; refreshKey?: number }) {
  const [records, setRecords] = useState<any[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  useEffect(() => {
    if (!brandId) return;
    (async () => {
      const r = await fetch(`${API}/brands/${brandId}/metrics`, { credentials: "include" });
      const j = await r.json().catch(() => []);
      setRecords(Array.isArray(j) ? j : []);
    })();
  }, [brandId, refreshKey]);

  async function deleteRecord(weekStart: string) {
    const ok = window.confirm(
      `Delete the weekly record for the week of ${weekStart}? This cannot be undone.`
    );
    if (!ok) return;
  
    const prev = records;
    setRecords((prev) => prev.filter((r) => r.week_start_date !== weekStart));
  
    const res = await fetch(`${API}/admin/brands/${brandId}/weekly/${weekStart}`, {
      method: "DELETE",
      credentials: "include",
    });
  
    if (!res.ok) {
      setRecords(prev); // rollback if delete failed
      alert("Failed to delete the weekly record. Please try again.");
    }
  }
  

  const refreshList = () => {
    setEditingId(null);
    fetch(`${API}/brands/${brandId}/metrics`, { credentials: "include" })
      .then(r => r.json())
      .then(j => setRecords(Array.isArray(j) ? j : []))
      .catch(() => setRecords([]));
  };

  if (records.length === 0) {
    return <div className="text-center py-8 text-gray-500"><p>No weekly orders data yet for this brand</p></div>;
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Week</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Sessions</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Orders</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Revenue</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Conv %</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.week_start_date} className="border-b hover:bg-gray-50">
                <td className="py-3 px-4">{safeDate(r.week_start_date)}</td>
                <td className="py-3 px-4 text-right">{r.sessions?.toLocaleString()}</td>
                <td className="py-3 px-4 text-right">{r.orders?.toLocaleString()}</td>
                <td className="py-3 px-4 text-right font-medium">
                  ${r.revenue_usd?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </td>
                <td className="py-3 px-4 text-right">{formatNumber(r.conversion_pct, 2)}%</td>
                <td className="py-3 px-4 text-right">
                  <button onClick={() => setEditingId(r.week_start_date)} className="text-xs px-3 py-1 rounded border border-blue-300 text-blue-600 hover:bg-blue-50 transition-colors mr-2">Edit</button>
                  <button onClick={() => deleteRecord(r.week_start_date)} className="text-xs px-3 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50 transition-colors">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editingId && (
        <EditWeeklyOrderModal
          brandId={brandId}
          weekStart={editingId}
          record={records.find(r => r.week_start_date === editingId)}
          onClose={() => setEditingId(null)}
          onSaved={refreshList}
        />
      )}
    </>
  );
}

// Weekly Subscriptions List
function WeeklySubsList({ brandId, refreshKey = 0 }: { brandId: string; refreshKey?: number }) {
  const [records, setRecords] = useState<any[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  useEffect(() => {
    if (!brandId) return;
    (async () => {
      const r = await fetch(`${API}/brands/${brandId}/subs-metrics`, { credentials: "include" });
      const j = await r.json().catch(() => []);
      setRecords(Array.isArray(j) ? j : []);
    })();
  }, [brandId, refreshKey]);

  async function deleteRecord(weekStart: string) {
    const ok = window.confirm(
      `Delete the subscription record for the week of ${safeDate(weekStart)}? This cannot be undone.`
    );
    if (!ok) return;
  
    const prev = records;
    // Optimistic UI: remove immediately
    setRecords((curr) => curr.filter((r) => r.week_start_date !== weekStart));
  
    try {
      const res = await fetch(
        `${API}/admin/brands/${brandId}/weekly/subscriptions/${weekStart}`,
        { method: "DELETE", credentials: "include" }
      );
  
      if (!res.ok) {
        // Roll back on failure
        setRecords(prev);
        alert("Failed to delete the subscription record. Please try again.");
      }
    } catch (e) {
      setRecords(prev);
      alert("Network error deleting the subscription record. Please try again.");
    }
  }
  

  const refreshList = () => {
    setEditingId(null);
    fetch(`${API}/brands/${brandId}/subs-metrics`, { credentials: "include" })
      .then(r => r.json())
      .then(j => setRecords(Array.isArray(j) ? j : []))
      .catch(() => setRecords([]));
  };

  if (records.length === 0) {
    return <div className="text-center py-8 text-gray-500"><p>No subscription data yet for this brand</p></div>;
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Week</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Start Subs</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">End Subs</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Churn %</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Sub LTV</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.week_start_date} className="border-b hover:bg-gray-50">
                <td className="py-3 px-4">{safeDate(r.week_start_date)}</td>
                <td className="py-3 px-4 text-right">{r.start_subs?.toLocaleString()}</td>
                <td className="py-3 px-4 text-right font-medium">{r.end_subs?.toLocaleString()}</td>
                <td className="py-3 px-4 text-right">{formatNumber(r.churn_pct, 2)}%</td>
                <td className="py-3 px-4 text-right">
                  ${r.subscriber_ltv_usd?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </td>
                <td className="py-3 px-4 text-right">
                  <button onClick={() => setEditingId(r.week_start_date)} className="text-xs px-3 py-1 rounded border border-blue-300 text-blue-600 hover:bg-blue-50 transition-colors mr-2">Edit</button>
                  <button onClick={() => deleteRecord(r.week_start_date)} className="text-xs px-3 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50 transition-colors">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editingId && (
        <EditWeeklySubsModal
          brandId={brandId}
          weekStart={editingId}
          record={records.find(r => r.week_start_date === editingId)}
          onClose={() => setEditingId(null)}
          onSaved={refreshList}
        />
      )}
    </>
  );
}

/* ---------------------- KPI Target Components (unchanged) ---------------------- */

function KpiTargetForm({
  brandId,
  brands,
  onBrandChange,
  onSaved,
}: {
  brandId: string;
  brands: Brand[];
  onBrandChange: (id: string) => void;
  onSaved?: () => void;
}) {
  const [kpi, setKpi] = useState(KPI_CATALOG[0]?.key || "");
  const [unit, setUnit] = useState<string>(findKpiMeta(KPI_CATALOG[0]?.key || "")?.unit || "$");
  const [targetValue, setTargetValue] = useState<number>(0);
  const [weekStart, setWeekStart] = useState<string>("");
  const [latestOrders, setLatestOrders] = useState<any>(null);
  const [latestSubs, setLatestSubs] = useState<any>(null);
  const [currentValue, setCurrentValue] = useState<number | null>(null);

  useEffect(() => {
    if (!brandId) return;
    fetchCurrentValues(brandId).then(({ latestOrders, latestSubs }) => {
      setLatestOrders(latestOrders);
      setLatestSubs(latestSubs);
    });
  }, [brandId]);

  useEffect(() => {
    const m = findKpiMeta(kpi);
    if (m) setUnit(m.unit);
  }, [kpi]);

  useEffect(() => {
    setCurrentValue(currentForKpi(kpi, latestOrders, latestSubs));
  }, [kpi, latestOrders, latestSubs]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!brandId) return;
    const res = await fetch(`${API}/admin/brands/${brandId}/targets/upsert`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ kpi, target_value: targetValue, unit, effective_week_start: weekStart }),
    });
    if (res.ok) onSaved?.();
  }

  return (
    <form onSubmit={save} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <Select label="Brand" name="target_brand_id" value={brandId} onChange={(e) => onBrandChange(e.target.value)}>
          {brands.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
        </Select>
        <Input label="Week Start" name="week_start" type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} required />
      </div>

      <Select label="KPI" name="kpi" value={kpi} onChange={(e) => setKpi(e.target.value)}>
        {KPI_CATALOG.map((k) => (<option key={k.key} value={k.key}>{k.label}</option>))}
      </Select>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Target Value <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            step="0.0001"
            value={targetValue}
            onChange={(e) => setTargetValue(Number(e.target.value))}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <Input label="Unit" name="unit" value={unit} onChange={(e) => setUnit(e.target.value)} />
      </div>

      {currentValue !== null && (
        <div className="text-xs text-zinc-600 bg-gray-50 p-3 rounded">
          Current value:{" "}
          <strong>
            {unit === "$"
              ? `$${currentValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
              : unit === "%"
              ? `${currentValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`
              : currentValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </strong>
        </div>
      )}

      <Button type="submit">Save Target</Button>
    </form>
  );
}

function KpiTargetList({ brandId, refreshKey = 0 }: { brandId: string; refreshKey?: number }) {
  const [rows, setRows] = useState<BrandTarget[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draftValue, setDraftValue] = useState<string>("");
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    if (!brandId) return;
    (async () => {
      const r = await fetch(`${API}/admin/brands/${brandId}/targets`, { credentials: "include" });
      const j = await r.json().catch(() => []);
      setRows(Array.isArray(j) ? j : []);
    })();
  }, [brandId, refreshKey]);

  function keyOf(r: BrandTarget) {
    return `${r.kpi}__${r.effective_week_start}`;
  }

  function formatVal(val: number | null, unit?: string) {
    if (val == null) return "—";
    const num = Number(val);
    if (unit === "$") return `$${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    if (unit === "%") return `${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  function startEdit(r: BrandTarget) {
    setEditingKey(keyOf(r));
    setDraftValue(r.target_value != null ? String(r.target_value) : "");
    setErrorKey(null);
  }

  function cancelEdit() {
    setEditingKey(null);
    setDraftValue("");
    setErrorKey(null);
  }

  async function commitEdit(r: BrandTarget) {
    const k = keyOf(r);
    if (savingKey === k) return;
    setSavingKey(k);
    setErrorKey(null);
  
    // Parse number (empty -> null)
    const parsed = draftValue.trim() === "" ? null : Number(draftValue);
    if (draftValue.trim() !== "" && Number.isNaN(parsed)) {
      setErrorKey(k);
      setSavingKey(null);
      return;
    }
  
    // Optimistic update
    const prevRows = rows;
    setRows((curr) => curr.map((row) => (keyOf(row) === k ? { ...row, target_value: parsed } : row)));
  
    // Build payload for the upsert endpoint.
    // IMPORTANT: omit `unit` so existing unit is preserved server-side.
    const payload: any = {
      kpi: r.kpi,
      effective_week_start: r.effective_week_start, // ISO is fine; server casts to ::date
      target_value: parsed,
      // unit: undefined  <-- omitted on purpose
    };
  
    try {
      const resp = await fetch(`${API}/admin/brands/${brandId}/targets/upsert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
  
      if (!resp.ok) throw new Error(await resp.text());
  
      // If your API returns the updated row (recommended), use it to sync any server-side formatting/units.
      if (resp.status !== 204) {
        const updated = await resp.json().catch(() => null);
        if (updated) {
          setRows((curr) =>
            curr.map((row) => (keyOf(row) === k ? { ...row, ...updated } : row))
          );
        }
      }
  
      setEditingKey(null);
      setDraftValue("");
    } catch (_e) {
      // Rollback on error
      setRows(prevRows);
      setErrorKey(k);
    } finally {
      setSavingKey(null);
    }
  }

  async function deleteTarget(row: BrandTarget) {
    const ok = window.confirm(
      `Delete target for "${row.kpi}" (week of ${row.effective_week_start})?\nThis cannot be undone.`
    );
    if (!ok) return;
  
    const prev = rows; // store for rollback
    setRows((prev) => prev.filter((r) => keyOf(r) !== keyOf(row)));
  
    const res = await fetch(`${API}/admin/brands/${brandId}/targets`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        kpi: row.kpi,
        effective_week_start: row.effective_week_start,
      }),
    });
  
    if (!res.ok) {
      setRows(prev); // rollback
      alert("Failed to delete the target. Please try again.");
    }
  }
  

  if (rows.length === 0) {
    return <div className="text-center py-8 text-gray-500"><p>No targets set yet for this brand</p></div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">KPI</th>
            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Target</th>
            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Week</th>
            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const k = keyOf(r);
            const meta = findKpiMeta(r.kpi);
            const unit = r.unit || meta?.unit || "";
            const isEditing = editingKey === k;
            const isSaving = savingKey === k;
            const hasError = errorKey === k;

            return (
              <tr key={k} className="border-b hover:bg-gray-50">
                <td className="py-3 px-4 text-sm text-gray-900">{meta?.label || r.kpi}</td>

                {/* Inline editable cell */}
                <td className="py-3 px-4 text-sm text-right text-gray-900 font-medium">
                  {isEditing ? (
                    <input
                      autoFocus
                      type="number"
                      step="any"
                      value={draftValue}
                      onChange={(e) => setDraftValue(e.target.value)}
                      onBlur={() => commitEdit(r)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); commitEdit(r); }
                        if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                      }}
                      className={`w-32 rounded-md border px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 ${
                        hasError ? "border-red-400 focus:ring-red-300" : "border-gray-300 focus:ring-blue-500"
                      }`}
                      disabled={isSaving}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => startEdit(r)}
                      title="Click to edit"
                      className="inline-flex items-center justify-end w-full cursor-text group"
                    >
                      <span className="tabular-nums group-hover:underline decoration-dotted">
                        {formatVal(r.target_value, unit)}
                      </span>
                      {isSaving && <span className="ml-2 text-xs text-gray-400">Saving…</span>}
                      {hasError && <span className="ml-2 text-xs text-red-600">Error</span>}
                    </button>
                  )}
                </td>

                <td className="py-3 px-4 text-sm text-gray-600">
                  {safeDate(r.effective_week_start)}
                </td>

                <td className="py-3 px-4 text-right">
                  <button
                    onClick={() => deleteTarget(r)}
                    className="text-xs px-3 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
                    disabled={isSaving || isEditing}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="mt-2 text-xs text-gray-500">Tip: click a target to edit. Enter/blur saves, Esc cancels.</p>
    </div>
  );
}

/* ---------------------- Tasks: Admin manage + Incoming ---------------------- */

function TaskForm({
  brandId,
  brands,
  onBrandChange,
  onSaved,
}: {
  brandId: string;
  brands: Brand[];
  onBrandChange: (id: string) => void;
  onSaved?: () => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("open");
  const [kpi, setKpi] = useState("");
  const [dueDate, setDueDate] = useState("");

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!brandId) return;
    const res = await fetch(`${API}/admin/brands/${brandId}/tasks`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ title, description: description || null, status, kpi: kpi || null, due_date: dueDate || null }),
    });
    if (res.ok) {
      setTitle(""); setDescription(""); setStatus("open"); setKpi(""); setDueDate("");
      onSaved?.();
    }
  }

  return (
    <form onSubmit={save} className="space-y-4">
      <Select label="Brand" name="task_brand_id" value={brandId} onChange={(e) => onBrandChange(e.target.value)}>
        {brands.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
      </Select>
      <Input label="Task Title" name="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Reduce churn by improving cancellation flow" required />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional: Add more details about this task..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
        />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <Select label="Status" name="status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="blocked">Blocked</option>
          <option value="completed">Completed</option>
        </Select>
        <Select label="Related KPI (Optional)" name="kpi" value={kpi} onChange={(e) => setKpi(e.target.value)}>
          <option value="">None</option>
          {KPI_CATALOG.map((k) => (<option key={k.key} value={k.key}>{k.label}</option>))}
        </Select>
        <Input label="Due Date (Optional)" name="due_date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
      </div>
      <Button type="submit">Create Task</Button>
    </form>
  );
}

function toDateInputValue(d?: string | null) {
  if (!d) return "";
  return typeof d === "string" ? d.slice(0, 10) : "";
}


function TaskList({ brandId, refreshKey = 0 }: { brandId: string; refreshKey?: number }) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [editingDescId, setEditingDescId] = useState<string | null>(null);
  const [draftDesc, setDraftDesc] = useState<string>("");

  const updateTaskPriority = (taskId: string, newPriority: Task["priority"]) =>
    patchTask(taskId, { priority: newPriority });

  const updateTaskDueDate = (taskId: string, newDate: string) =>
    patchTask(taskId, { due_date: newDate ? newDate : null });  
  

  useEffect(() => {
    if (!brandId) return;
    (async () => {
      const r = await fetch(`${API}/admin/brands/${brandId}/tasks`, { credentials: "include" });
      const j = await r.json().catch(() => []);
      setTasks(Array.isArray(j) ? j : []);
    })();
  }, [brandId, refreshKey]);

  // Generic PATCH helper (optimistic)
  async function patchTask(taskId: string, patch: Partial<Task>) {
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, ...patch } : t)));
    const res = await fetch(`${API}/admin/brands/${brandId}/tasks/${taskId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      // revert on failure
      const r = await fetch(`${API}/admin/brands/${brandId}/tasks`, { credentials: "include" });
      const j = await r.json().catch(() => []);
      setTasks(Array.isArray(j) ? j : []);
    }
  }

  const updateTaskStatus = (taskId: string, newStatus: string) =>
    patchTask(taskId, { status: newStatus });

  const updateTaskKpi = (taskId: string, newKpi: string) =>
    patchTask(taskId, { kpi: newKpi || null }); // "" => clear KPI

  const updateTaskDescription = async (taskId: string, newDesc: string) => {
    const clean = newDesc.trim();
    await patchTask(taskId, clean ? { description: clean } : { description: "" });
  };

  function startEditingDesc(task: Task) {
    setEditingDescId(task.id);
    setDraftDesc(task.description ?? "");
  }

  async function saveEditingDesc(task: Task) {
    const newDesc = draftDesc;
    setEditingDescId(null);
    await updateTaskDescription(task.id, newDesc);
  }

  function cancelEditingDesc() {
    setEditingDescId(null);
    setDraftDesc("");
  }

  async function deleteTask(taskId: string) {
    const ok = window.confirm("Delete this task? This cannot be undone.");
    if (!ok) return;
  
    // optimistic remove
    const prev = tasks;
    setTasks(prev => prev.filter(t => t.id !== taskId));
  
    const res = await fetch(`${API}/admin/brands/${brandId}/tasks/${taskId}`, {
      method: "DELETE",
      credentials: "include",
    });
  
    if (!res.ok) {
      // rollback on failure
      setTasks(prev);
      alert("Failed to delete the task. Please try again.");
    }
  }
  

  if (tasks.length === 0) {
    return <div className="text-center py-8 text-gray-500"><p>No tasks for this brand yet</p></div>;
  }

  const groupedTasks = {
    open: tasks.filter(t => t.status === "open"),
    in_progress: tasks.filter(t => t.status === "in_progress"),
    blocked: tasks.filter(t => t.status === "blocked"),
    completed: tasks.filter(t => t.status === "completed"),
  };

  const statusConfig: Record<string, { label: string; color: string }> = {
    open: { label: "Open", color: "bg-blue-100 text-blue-700" },
    in_progress: { label: "In Progress", color: "bg-yellow-100 text-yellow-700" },
    blocked: { label: "Blocked", color: "bg-red-100 text-red-700" },
    completed: { label: "Completed", color: "bg-green-100 text-green-700" },
  };

  return (
    <div className="space-y-6">
      {Object.entries(groupedTasks).map(([status, taskList]) => {
        if (taskList.length === 0) return null;
        return (
          <div key={status}>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              {statusConfig[status].label} ({taskList.length})
            </h3>
            <div className="space-y-2">
              {taskList.map(task => {
                const isEditing = editingDescId === task.id;
                return (
                  <div key={task.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all">
                    <div className="flex flex-col-reverse sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 mb-1">{task.title}</div>

                        {/* Description (click to edit) */}
                        <div className="mb-2">
                          {!isEditing ? (
                            <button
                              type="button"
                              onClick={() => startEditingDesc(task)}
                              className={`text-left w-full group ${
                                task.description ? "text-sm text-gray-700" : "text-sm text-gray-400 italic"
                              }`}
                              title="Click to edit description"
                            >
                              {task.description ? task.description : "Add a description"}
                              <span className="ml-2 opacity-0 group-hover:opacity-100 text-xs text-blue-500 underline">
                                Edit
                              </span>
                            </button>
                          ) : (
                            <div className="space-y-2">
                              <textarea
                                autoFocus
                                value={draftDesc}
                                onChange={(e) => setDraftDesc(e.target.value)}
                                onKeyDown={(e) => {
                                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                                    e.preventDefault();
                                    void saveEditingDesc(task);
                                  } else if (e.key === "Escape") {
                                    e.preventDefault();
                                    cancelEditingDesc();
                                  }
                                }}
                                onBlur={() => void saveEditingDesc(task)}
                                rows={3}
                                className="w-full text-sm border border-blue-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                placeholder="Describe the task details, context, or acceptance criteria…"
                              />
                              <div className="w-full sm:w-auto grid grid-cols-1 xs:grid-cols-2 sm:flex gap-2">
                                <button
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => void saveEditingDesc(task)}
                                  className="text-xs px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                                >
                                  Save
                                </button>
                                <button
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={cancelEditingDesc}
                                  className="text-xs px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                                >
                                  Cancel
                                </button>
                                <span className="text-xs text-gray-400 ml-2">Press ⌘/Ctrl+Enter to save • Esc to cancel</span>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          <span className={`px-2 py-1 rounded-full ${statusConfig[status].color}`}>{statusConfig[status].label}</span>
                          {task.created_by_role === "brand" && (task.approved === false || task.status === "requested") && (
                            <span className="px-2 py-1 rounded-full border bg-gray-100 text-gray-700 border-gray-200">Pending approval</span>
                          )}
                          {task.kpi && <span className="px-2 py-1 bg-gray-100 rounded">{findKpiMeta(task.kpi)?.label || task.kpi}</span>}
                          {task.due_date && <span className="text-gray-500">Due: {safeDate(task.due_date)}</span>}
                          {task.priority && (
                          <span
                            className={`px-2 py-1 rounded border text-xs ${
                              task.priority === "urgent"
                                ? "bg-red-50 text-red-700 border-red-200"
                                : task.priority === "high"
                                ? "bg-orange-50 text-orange-700 border-orange-200"
                                : task.priority === "normal"
                                ? "bg-gray-50 text-gray-700 border-gray-200"
                                : "bg-slate-50 text-slate-700 border-slate-200"
                            }`}
                            title="Priority"
                          >
                            {task.priority}
                          </span>
                        )}
                        </div>
                      </div>

                      <div className="w-full sm:w-auto grid grid-cols-1 xs:grid-cols-2 sm:flex gap-2">
                        {/* Status dropdown */}
                        <select
                          value={task.status}
                          onChange={(e) => updateTaskStatus(task.id, e.target.value)}
                          className="w-full sm:w-auto text-sm h-9 px-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          title="Change status"
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="blocked">Blocked</option>
                          <option value="completed">Completed</option>
                        </select>

                        {/* Priority dropdown */}
                        <select
                          value={task.priority || "normal"}
                          onChange={(e) => updateTaskPriority(task.id, e.target.value as Task["priority"])}
                          className="w-full sm:w-auto text-sm h-9 px-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          title="Change priority"
                        >
                          <option value="low">Priority: Low</option>
                          <option value="normal">Priority: Normal</option>
                          <option value="high">Priority: High</option>
                          <option value="urgent">Priority: Urgent</option>
                        </select>

                        {/* KPI dropdown */}
                        <select
                          value={task.kpi || ""}
                          onChange={(e) => updateTaskKpi(task.id, e.target.value)}
                          className="text-xs px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          title="Change related KPI"
                        >
                          <option value="">No KPI</option>
                          {KPI_CATALOG.map(k => (
                            <option key={k.key} value={k.key}>{k.label}</option>
                          ))}
                        </select>

                        {/* Due date editor */}
                        <input
                          type="date"
                          value={toDateInputValue(task.due_date)}
                          onChange={(e) => updateTaskDueDate(task.id, e.target.value)}
                          className="w-full sm:w-auto text-sm h-9 px-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          title="Set due date"
                        />

                        {/* Optional: quick clear */}
                        <button
                          type="button"
                          onClick={() => updateTaskDueDate(task.id, "")}
                          className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                          title="Clear due date"
                        >
                          Clear
                        </button>


                        <button
                          onClick={() => deleteTask(task.id)}
                          className="text-xs px-2 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function InProgressTasks({ brandId, refreshKey = 0 }: { brandId: string; refreshKey?: number }) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!brandId) return;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/admin/brands/${brandId}/tasks?status=in_progress`, { credentials: "include" });
        const j = await r.json().catch(() => []);
        const arr = Array.isArray(j) ? j : [];
        // sort by due_date asc, nulls last
        arr.sort((a: Task, b: Task) => {
          const sa = a.due_date ? a.due_date.slice(0, 10) : "9999-12-31";
          const sb = b.due_date ? b.due_date.slice(0, 10) : "9999-12-31";
          return sa.localeCompare(sb);
        });
        setTasks(arr);
      } finally {
        setLoading(false);
      }
    })();
  }, [brandId, refreshKey]);

  // local PATCH helper (optimistic), mirrors your TaskList approach
  async function patchTask(taskId: string, patch: Partial<Task>) {
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, ...patch } : t)));
    const res = await fetch(`${API}/admin/brands/${brandId}/tasks/${taskId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      // reload this list on failure
      const r = await fetch(`${API}/admin/brands/${brandId}/tasks?status=in_progress`, { credentials: "include" });
      const j = await r.json().catch(() => []);
      setTasks(Array.isArray(j) ? j : []);
    }
  }

  const setStatus = (id: string, status: Task["status"]) => patchTask(id, { status });
  const setPriority = (id: string, priority: Task["priority"]) => patchTask(id, { priority });
  const setDueDate = (id: string, date: string) => patchTask(id, { due_date: date || null });

  function toDateInputValue(d?: string | null) {
    if (!d) return "";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toISOString().slice(0, 10);
  }

  if (loading) return <div className="text-gray-500">Loading…</div>;
  if (!tasks.length) return <div className="text-gray-500">No tasks in progress.</div>;

  return (
    <div className="space-y-2">
      {tasks.map(t => (
        <div key={t.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900">{t.title}</div>
              {t.description && (
                <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap break-words">
                  {t.description}
                </p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200">In Progress</span>
                {t.kpi && <span className="px-2 py-0.5 rounded bg-gray-50 border border-gray-200">{findKpiMeta(t.kpi)?.label || t.kpi}</span>}
                {t.priority && <span className="px-2 py-0.5 rounded bg-gray-50 border border-gray-200 capitalize">Priority: {t.priority}</span>}
                {t.due_date && <span className="px-2 py-0.5 rounded bg-gray-50 border border-gray-200">Due {safeDate(t.due_date)}</span>}
                <span className="px-2 py-0.5 rounded bg-gray-50 border border-gray-200">ID: {t.id}</span>
              </div>
            </div>

            <div className="w-full sm:w-auto grid grid-cols-1 xs:grid-cols-2 sm:flex gap-2">
              {/* quick status toggle to Completed / Blocked */}
              <select
                value={t.status}
                onChange={(e) => setStatus(t.id, e.target.value as Task["status"])}
                className="w-full sm:w-auto text-sm h-9 px-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                title="Change status"
              >
                <option value="in_progress">In Progress</option>
                <option value="open">Open</option>
                <option value="blocked">Blocked</option>
                <option value="completed">Completed</option>
              </select>

              {/* priority */}
              <select
                value={t.priority || "normal"}
                onChange={(e) => setPriority(t.id, e.target.value as Task["priority"])}
                className="w-full sm:w-auto text-sm h-9 px-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                title="Change priority"
              >
                <option value="low">Priority: Low</option>
                <option value="normal">Priority: Normal</option>
                <option value="high">Priority: High</option>
                <option value="urgent">Priority: Urgent</option>
              </select>

              {/* due date */}
              <input
                type="date"
                value={toDateInputValue(t.due_date)}
                onChange={(e) => setDueDate(t.id, e.target.value)}
                className="w-full sm:w-auto text-sm h-9 px-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                title="Set due date"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------------- North Star Management ---------------------- */

function NorthStarForm({
  brandId,
  brands,
  onBrandChange,
  onSaved,
}: {
  brandId: string;
  brands: Brand[];
  onBrandChange: (id: string) => void;
  onSaved?: () => void;
}) {
  const [goal, setGoal] = useState("");
  const [targetValue, setTargetValue] = useState<number | null>(null);
  const [metricType, setMetricType] = useState("revenue");
  const [deadline, setDeadline] = useState("");
  const [notes, setNotes] = useState("");

  const metricOptions = [
    { value: "revenue", label: "Revenue ($)" },
    { value: "active_subs", label: "Active Subscribers" },
    { value: "orders", label: "Orders" },
    { value: "customer_ltv", label: "Customer LTV ($)" },
  ];

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!brandId || !goal.trim()) return;

    const body = {
      goal: goal.trim(),
      target_value: targetValue,
      metric_type: metricType,
      deadline: deadline || null,
      notes: notes.trim() || null,
    };

    const res = await fetch(`${API}/admin/brands/${brandId}/north-stars`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });

    if (res.ok) {
      setGoal("");
      setTargetValue(null);
      setMetricType("revenue");
      setDeadline("");
      setNotes("");
      onSaved?.();
    }
  }

  return (
    <form onSubmit={save} className="space-y-4">
      <Select label="Brand" name="ns_brand_id" value={brandId} onChange={(e) => onBrandChange(e.target.value)}>
        {brands.map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        ))}
      </Select>

      <Input
        label="Goal Statement"
        name="goal"
        value={goal}
        onChange={(e) => setGoal(e.target.value)}
        placeholder="e.g., Reach $1M in annual revenue"
        required
      />

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Target Value
          </label>
          <input
            type="number"
            step="0.01"
            value={targetValue ?? ""}
            onChange={(e) => setTargetValue(e.target.value ? Number(e.target.value) : null)}
            placeholder="1000000"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <Select
          label="Metric Type"
          name="metric_type"
          value={metricType}
          onChange={(e) => setMetricType(e.target.value)}
        >
          {metricOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </div>

      <Input
        label="Deadline"
        name="deadline"
        type="date"
        value={deadline}
        onChange={(e) => setDeadline(e.target.value)}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Additional context about this goal..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <Button type="submit">Create North Star Goal</Button>
    </form>
  );
}

function NorthStarList({ brandId, refreshKey = 0 }: { brandId: string; refreshKey?: number }) {
  const [northStars, setNorthStars] = useState<NorthStar[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<NorthStar>>({});

  useEffect(() => {
    if (!brandId) return;
    (async () => {
      const r = await fetch(`${API}/admin/brands/${brandId}/north-stars`, {
        credentials: "include",
      });
      const j = await r.json().catch(() => []);
      setNorthStars(Array.isArray(j) ? j : []);
    })();
  }, [brandId, refreshKey]);

  function startEdit(ns: NorthStar) {
    setEditingId(ns.id);
    setEditForm({
      goal: ns.goal,
      target_value: ns.target_value,
      metric_type: ns.metric_type,
      deadline: ns.deadline ? ns.deadline.slice(0, 10) : "",
      notes: ns.notes,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm({});
  }

  async function saveEdit(id: string) {
    const res = await fetch(`${API}/admin/north-stars/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(editForm),
    });

    if (res.ok) {
      const updated = await res.json();
      setNorthStars((prev) => prev.map((ns) => (ns.id === id ? updated : ns)));
      cancelEdit();
    }
  }

  async function completeGoal(id: string) {
    const ok = window.confirm("Mark this North Star as completed?");
    if (!ok) return;

    const res = await fetch(`${API}/admin/north-stars/${id}/complete`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({}),
    });

    if (res.ok) {
      const updated = await res.json();
      setNorthStars((prev) => prev.map((ns) => (ns.id === id ? updated : ns)));
    }
  }

  async function archiveGoal(id: string) {
    const ok = window.confirm("Archive this North Star goal?");
    if (!ok) return;

    const res = await fetch(`${API}/admin/north-stars/${id}/archive`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });

    if (res.ok) {
      const updated = await res.json();
      setNorthStars((prev) => prev.map((ns) => (ns.id === id ? updated : ns)));
    }
  }

  async function deleteGoal(id: string) {
    const ok = window.confirm(
      "Permanently delete this North Star? This cannot be undone."
    );
    if (!ok) return;

    const prev = northStars;
    setNorthStars((curr) => curr.filter((ns) => ns.id !== id));

    const res = await fetch(`${API}/admin/north-stars/${id}`, {
      method: "DELETE",
      credentials: "include",
    });

    if (!res.ok) {
      setNorthStars(prev);
      alert("Failed to delete. Please try again.");
    }
  }

  function formatValue(value: number | null, metricType?: string | null) {
    if (value == null) return "—";
    if (metricType === "revenue" || metricType?.includes("ltv")) {
      return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    }
    return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      active: "bg-green-100 text-green-700 border-green-200",
      completed: "bg-blue-100 text-blue-700 border-blue-200",
      abandoned: "bg-gray-100 text-gray-700 border-gray-200",
      archived: "bg-gray-100 text-gray-500 border-gray-200",
    };
    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
          colors[status] || colors.archived
        }`}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (northStars.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No North Star goals set for this brand yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {northStars.map((ns) => {
        const isEditing = editingId === ns.id;
        const isActive = ns.status === "active";

        return (
          <div
            key={ns.id}
            className={`border rounded-lg p-4 transition-all ${
              isActive
                ? "border-blue-300 bg-blue-50/30"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            {!isEditing ? (
               <>
               <div className="flex flex-col gap-3">
                 {/* Top row: status + actions (actions wrap nicely) */}
                 <div className="flex flex-wrap items-center justify-between gap-2">
                   <div className="flex items-center gap-2">
                     {statusBadge(ns.status)}
                     {ns.status === "active" && (
                       <span className="text-xs font-semibold text-blue-600">🎯 ACTIVE GOAL</span>
                     )}
                   </div>
                   <div className="flex flex-wrap gap-2">
                     <button
                       onClick={() => startEdit(ns)}
                       className="text-xs px-3 py-1 rounded border border-blue-300 text-blue-600 hover:bg-blue-50"
                     >
                       Edit
                     </button>
                     {ns.status === "active" && (
                       <button
                         onClick={() => completeGoal(ns.id)}
                         className="text-xs px-3 py-1 rounded border border-green-300 text-green-600 hover:bg-green-50"
                       >
                         Complete
                       </button>
                     )}
                     {ns.status !== "archived" && (
                       <button
                         onClick={() => archiveGoal(ns.id)}
                         className="text-xs px-3 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                       >
                         Archive
                       </button>
                     )}
                     <button
                       onClick={() => deleteGoal(ns.id)}
                       className="text-xs px-3 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50"
                     >
                       Delete
                     </button>
                   </div>
                 </div>
         
                 {/* Goal text */}
                 <div className="font-medium text-gray-900 break-words">{ns.goal}</div>
         
                 {/* Meta line(s) */}
                 <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                   {ns.target_value != null && (
                     <span className="px-2 py-0.5 rounded bg-gray-50 border border-gray-200">
                       Target: {formatValue(ns.target_value, ns.metric_type)}
                     </span>
                   )}
                   {ns.deadline && (
                     <span className="px-2 py-0.5 rounded bg-gray-50 border border-gray-200">
                       Deadline: {safeDate(ns.deadline)}
                     </span>
                   )}
                   {ns.completed_at && (
                     <span className="px-2 py-0.5 rounded bg-gray-50 border border-gray-200">
                       Completed: {safeDate(ns.completed_at)}
                     </span>
                   )}
                 </div>
         
                 {ns.notes && (
                   <div className="text-sm text-gray-500 whitespace-pre-wrap break-words">
                     {ns.notes}
                   </div>
                 )}
               </div>
             </>
            ) : (
              <div className="space-y-3">
                <Input
                  label="Goal Statement"
                  name="edit_goal"
                  value={editForm.goal || ""}
                  onChange={(e) => setEditForm({ ...editForm, goal: e.target.value })}
                />
                <div className="grid md:grid-cols-2 gap-3">
                  <Input
                    label="Target Value"
                    name="edit_target"
                    type="number"
                    step="0.01"
                    value={editForm.target_value ?? ""}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        target_value: e.target.value ? Number(e.target.value) : null,
                      })
                    }
                  />
                  <Select
                    label="Metric Type"
                    name="edit_metric"
                    value={editForm.metric_type || "revenue"}
                    onChange={(e) =>
                      setEditForm({ ...editForm, metric_type: e.target.value })
                    }
                  >
                    <option value="revenue">Revenue ($)</option>
                    <option value="active_subs">Active Subscribers</option>
                    <option value="orders">Orders</option>
                    <option value="customer_ltv">Customer LTV ($)</option>
                  </Select>
                </div>
                <Input
                  label="Deadline"
                  name="edit_deadline"
                  type="date"
                  value={editForm.deadline || ""}
                  onChange={(e) => setEditForm({ ...editForm, deadline: e.target.value })}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={editForm.notes || ""}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => saveEdit(ns.id)}
                    className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


/* ---------------------- Main Component ---------------------- */

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [msg, setMsg] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const [selectedBrandId, setSelectedBrandId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"home" | "brands" | "northstar" | "data" | "targets" | "tasks" | "leads" | "incoming" | "funnel">("home");

  const [targetsTick, setTargetsTick] = useState(0);
  const [tasksTick, setTasksTick] = useState(0);
  const [ordersTick, setOrdersTick] = useState(0);
  const [subsTick, setSubsTick] = useState(0);
  const [northStarTick, setNorthStarTick] = useState(0); 

  const [leadsTick, setLeadsTick] = useState(0);


  if (!user) {
    return (
      <div className="min-h-screen grid place-items-center bg-gray-50 p-6">
        <LoginForm redirectTo="/dashboard" />
      </div>
    );
  }

  if (user.role !== "admin") {
    return (
      <div className="min-h-screen grid place-items-center bg-gray-50 p-6">
        <div className="bg-white p-6 rounded-lg border shadow-sm max-w-md text-center">
          <div className="text-xl font-semibold mb-2">Access Denied</div>
          <div className="text-sm text-zinc-600">You need an admin account to view this page.</div>
        </div>
      </div>
    );
  }

  useEffect(() => {
    (async () => {
      const [b, u] = await Promise.all([
        fetch(`${API}/admin/brands`, { credentials: "include" }).then(r => r.json()),
        fetch(`${API}/admin/users`, { credentials: "include" }).then(r => r.json()),
      ]);
      setBrands(b);
      setUsers(u);
      if (b?.length) setSelectedBrandId(b[0].id);
    })();
  }, [user?.id]);

  const userOptions = useMemo(
    () => users.map(u => ({ value: u.id, label: `${u.email}${u.name ? ` (${u.name})` : ""}` })),
    [users]
  );

  const showMessage = (text: string, type: "success" | "error") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 5000);
  };

  function field(form: HTMLFormElement, name: string) {
    return (form.elements.namedItem(name) as HTMLInputElement)?.value;
  }

  async function createBrand(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = e.currentTarget;
    const body = { name: field(f, "name"), slug: field(f, "slug"), currency: field(f, "currency") || "USD" };
    const res = await fetch(`${API}/admin/brands`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body),
    });
    if (res.ok) {
      const b = await res.json();
      setBrands(prev => [...prev, b].sort((a,b)=>a.name.localeCompare(b.name)));
      setSelectedBrandId(b.id);
      showMessage("Brand created successfully", "success");
      f.reset();
    } else showMessage("Failed to create brand", "error");
  }

  async function inviteUser(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = e.currentTarget;
    const body = { email: field(f, "email"), role: field(f, "role") || "member", brand_id: field(f, "invite_brand_id") || null };
    const res = await fetch(`${API}/admin/invites`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body),
    });
    showMessage(res.ok ? "Invite sent successfully" : "Failed to send invite", res.ok ? "success" : "error");
    if (res.ok) f.reset();
  }

  async function attachMember(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = e.currentTarget;
    const bid = field(f, "brand_id");
    const uid = field(f, "user_id");
    const role = field(f, "member_role") || "member";
    const res = await fetch(`${API}/admin/brands/${bid}/users`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ user_id: uid, role }),
    });
    showMessage(res.ok ? "Member attached successfully" : "Failed to attach member", res.ok ? "success" : "error");
  }

  async function saveWeeklyOrders(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = e.currentTarget;
    const bid = field(f, "wo_brand_id");
    const week_start = field(f, "wo_week_start");

    const body = {
      week_start,
      sessions: Number(field(f, "sessions") || 0),
      atc: Number(field(f, "atc") || 0),
      checkout: Number(field(f, "checkout") || 0),
      orders: Number(field(f, "orders") || 0),
      revenue: Number(field(f, "revenue") || 0),
      ad_spend: Number(field(f, "ad_spend") || 0),
      new_customers: Number(field(f, "new_customers") || 0),
      repeat_customers: Number(field(f, "repeat_customers") || 0),
      gross_margin_pct: Number(field(f, "gross_margin_pct") || 0),
      total_shipping_charges: Number(field(f, "total_shipping_charges") || 0),
    };

    const res = await fetch(`${API}/admin/brands/${bid}/weekly/upsert`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify(body),
    });
    showMessage(res.ok ? "Weekly data saved successfully" : "Failed to save weekly data", res.ok ? "success" : "error");
    if (res.ok) { f?.reset(); setOrdersTick(t => t + 1); }
  }

  async function saveWeeklySubs(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = e.currentTarget;
    const bid = field(f, "ws_brand_id");
    const week_start = field(f, "ws_week_start");

    const toCents = (v: string) => Math.round((Number(v || 0)) * 100);

    const body = {
      week_start_date: week_start,
      start_subs: Number(field(f, "start_subs") || 0),
      new_subs: Number(field(f, "new_subs") || 0),
      churned_subs: Number(field(f, "churned_subs") || 0),
      sub_aov_cents: toCents(field(f, "sub_aov") || "0"),
      avg_active_days: Number(field(f, "avg_active_days") || 0),
      ad_spend_cents: toCents(field(f, "ad_spend") || "0"),
      gross_margin_pct: Number(field(f, "gross_margin_pct") || 0),
      fulfillment_per_order_avg_cents: toCents(field(f, "fulfillment_per_order_avg") || "0"),
    };

    const res = await fetch(`${API}/admin/brands/${bid}/weekly/subscriptions/upsert`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify(body),
    });
    showMessage(res.ok ? "Subscription data saved successfully" : "Failed to save subscription data", res.ok ? "success" : "error");
    if (res.ok) { f?.reset(); setSubsTick(t => t + 1); }
  }

  const tabs = [
    { id: "home", label: "Home" },
    { id: "brands", label: "Brands & Users" },
    { id: "northstar", label: "Brand Northstar" },
    { id: "data", label: "Weekly Data" },
    { id: "targets", label: "KPI Targets" },
    { id: "tasks", label: "Tasks" },
    { id: "leads", label: "Leads" },
    { id: "incoming", label: "Incoming Requests" },
    { id: "funnel", label: "Funnel" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-500 mt-1">{user?.email}</p>
            </div>
            <button
              onClick={async () => { await logout(); navigate("/", { replace: true }); }}
              className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              Log out
            </button>
          </div>
        </div>
      </header>

      {msg && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className={`p-4 rounded-lg ${msg.type === "success" ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-800"}`}>
            <div className="flex items-center gap-2">
              <span>{msg.type === "success" ? "✓" : "✕"}</span>
              <span className="font-medium">{msg.text}</span>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="border-b border-gray-200">
          {/* Mobile: horizontal scrollable pills; Desktop: classic underline */}
          <nav
            className="
              -mb-px flex md:flex-nowrap md:space-x-8
              overflow-x-auto md:overflow-x-visible
              no-scrollbar
              gap-2 md:gap-0
              snap-x snap-mandatory
              pb-1
            "
            aria-label="Admin tabs"
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={[
                  "shrink-0 snap-start",
                  // mobile pill
                  "md:hidden px-3 py-2 rounded-full text-sm",
                  activeTab === tab.id
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700",
                  // desktop underline
                  "md:block md:rounded-none md:bg-transparent md:text-sm md:px-1 md:py-4",
                  activeTab === tab.id
                    ? "md:border-b-2 md:border-blue-500 md:text-blue-600"
                    : "md:border-b-2 md:border-transparent md:text-gray-500 md:hover:text-gray-700 md:hover:border-gray-300",
                ].join(" ")}
              >
                <span className="whitespace-nowrap">{tab.label}</span>
              </button>
            ))}
            {/* Desktop-only duplicate of the same map to keep underline layout clean */}
            <div className="hidden md:flex md:space-x-8">
              {tabs.map((tab) => (
                <button
                  key={`${tab.id}-desktop`}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </nav>
        </div>
      </div>


      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === "home" && (
          <Card
            title="What we're working on (In Progress)"
            right={
              <Select label="Brand" name="home_brand_id" value={selectedBrandId} onChange={(e) => setSelectedBrandId(e.target.value)}>
                {brands.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
              </Select>
            }
          >
            <InProgressTasks brandId={selectedBrandId} />
          </Card>
        )}

        {activeTab === "brands" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <Card title="Create Brand">
              <form onSubmit={createBrand} className="space-y-4">
                <Input label="Brand Name" name="name" placeholder="e.g., Flourbox Bakery" required />
                <Input label="Slug" name="slug" placeholder="e.g., flourbox" required />
                <Input label="Currency" name="currency" placeholder="USD" defaultValue="USD" />
                <Button type="submit">Create Brand</Button>
              </form>
            </Card>

            <Card title="Invite User">
              <form onSubmit={inviteUser} className="space-y-4">
                <Input label="Email" name="email" type="email" placeholder="user@brand.com" required />
                <Select label="Role" name="role">
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </Select>
                <Select label="Brand (Optional)" name="invite_brand_id">
                  <option value="">No brand assignment</option>
                  {brands.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
                </Select>
                <Button type="submit">Send Invite</Button>
              </form>
            </Card>

            <Card title="Attach Member to Brand">
              <form onSubmit={attachMember} className="space-y-4">
                <Select label="Brand" name="brand_id" value={selectedBrandId} onChange={(e) => setSelectedBrandId(e.target.value)}>
                  {brands.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
                </Select>
                <Select label="User" name="user_id">
                  {userOptions.map((u) => (<option key={u.value} value={u.value}>{u.label}</option>))}
                </Select>
                <Select label="Role" name="member_role">
                  <option value="member">Member</option>
                  <option value="manager">Manager</option>
                </Select>
                <Button type="submit">Attach Member</Button>
              </form>
            </Card>

            <Card title="Users">
            <UsersTable api={API} />
            </Card>

          </div>
        )}

        {activeTab === "northstar" && (
          <Card title="North Star Goals">
            <NorthStarForm
              brandId={selectedBrandId}
              brands={brands}
              onBrandChange={setSelectedBrandId}
              onSaved={() => {
                setNorthStarTick((t) => t + 1);
                showMessage("North Star goal created successfully", "success");
              }}
            />
            <div className="mt-8 pt-8 border-t">
              <h4 className="text-lg font-semibold mb-4">Goal History</h4>
              <NorthStarList brandId={selectedBrandId} refreshKey={northStarTick} />
            </div>
          </Card>
        )}

        {activeTab === "data" && (
          <div className="space-y-6">
            <Card title="Weekly Orders Data">
              <form onSubmit={saveWeeklyOrders} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <Select label="Brand" name="wo_brand_id" value={selectedBrandId} onChange={(e) => setSelectedBrandId(e.target.value)}>
                    {brands.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
                  </Select>
                  <Input label="Week Start" name="wo_week_start" type="date" required />
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Funnel Metrics</h4>
                  <div className="grid md:grid-cols-4 gap-4">
                    <Input label="Sessions" name="sessions" type="number" placeholder="0" />
                    <Input label="Add to Cart" name="atc" type="number" placeholder="0" />
                    <Input label="Checkout" name="checkout" type="number" placeholder="0" />
                    <Input label="Orders" name="orders" type="number" placeholder="0" />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Revenue & Costs</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Input label="Revenue ($)" name="revenue" type="number" step="0.01" placeholder="0.00" />
                    <Input label="Ad Spend ($)" name="ad_spend" type="number" step="0.01" placeholder="0.00" />
                    <Input label="Shipping Charges ($)" name="total_shipping_charges" type="number" step="0.01" placeholder="0.00" />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Customers</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Input label="New Customers" name="new_customers" type="number" placeholder="0" />
                    <Input label="Repeat Customers" name="repeat_customers" type="number" placeholder="0" />
                    <Input label="Gross Margin % (0-1)" name="gross_margin_pct" type="number" step="0.0001" placeholder="0.50" />
                  </div>
                </div>

                <Button type="submit">Save Weekly Orders</Button>
              </form>
              <div className="mt-8 pt-8 border-t">
                <h4 className="text-lg font-semibold mb-4">Existing Records</h4>
                <WeeklyOrdersList brandId={selectedBrandId} refreshKey={ordersTick} />
              </div>
            </Card>

            <Card title="Weekly Subscription Data">
              <form onSubmit={saveWeeklySubs} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <Select label="Brand" name="ws_brand_id" value={selectedBrandId} onChange={(e) => setSelectedBrandId(e.target.value)}>
                    {brands.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
                  </Select>
                  <Input label="Week Start" name="ws_week_start" type="date" required />
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Subscription Metrics</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Input label="Start Subs" name="start_subs" type="number" placeholder="0" />
                    <Input label="New Subs" name="new_subs" type="number" placeholder="0" />
                    <Input label="Churned Subs" name="churned_subs" type="number" placeholder="0" />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Economics</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Input label="Sub AOV ($)" name="sub_aov" type="number" step="0.01" placeholder="0.00" />
                    <Input label="Avg Active Days" name="avg_active_days" type="number" placeholder="30" />
                    <Input label="Ad Spend ($)" name="ad_spend" type="number" step="0.01" placeholder="0.00" />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Costs</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input label="Gross Margin % (0-1)" name="gross_margin_pct" type="number" step="0.0001" placeholder="0.50" />
                    <Input label="Fulfillment per Order ($)" name="fulfillment_per_order_avg" type="number" step="0.01" placeholder="0.00" />
                  </div>
                </div>

                <Button type="submit">Save Subscription Data</Button>
              </form>
              <div className="mt-8 pt-8 border-t">
                <h4 className="text-lg font-semibold mb-4">Existing Records</h4>
                <WeeklySubsList brandId={selectedBrandId} refreshKey={subsTick} />
              </div>
            </Card>
          </div>
        )}

        {activeTab === "targets" && (
          <Card title="KPI Targets">
            <KpiTargetForm
              brandId={selectedBrandId}
              brands={brands}
              onBrandChange={setSelectedBrandId}
              onSaved={() => { setTargetsTick(t => t + 1); showMessage("Target saved successfully", "success"); }}
            />
            <div className="mt-8">
              <KpiTargetList brandId={selectedBrandId} refreshKey={targetsTick} />
            </div>
          </Card>
        )}

        {activeTab === "tasks" && (
          <Card title="Manage Tasks">
            <TaskForm
              brandId={selectedBrandId}
              brands={brands}
              onBrandChange={setSelectedBrandId}
              onSaved={() => { setTasksTick(t => t + 1); showMessage("Task created successfully", "success"); }}
            />
            <div className="mt-8">
              <TaskList brandId={selectedBrandId} refreshKey={tasksTick} />
            </div>
          </Card>
        )}

        {activeTab === "leads" && (
          <Card
          title="Leads (Light CRM)"
          right={
            <button
              onClick={() => setLeadsTick(t => t + 1)}
              className="text-sm px-3 py-1 border rounded hover:bg-gray-50"
            >
              Refresh
            </button>
          }
        >
          <div className="grid lg:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-semibold mb-3">Add Lead</h4>
              <LeadForm
                onSaved={() => setLeadsTick(t => t + 1)}
                users={users}
              />
            </div>
        
            {/* Mobile list */}
            <div className="lg:col-span-2">
              <h4 className="text-sm font-semibold mb-3 md:hidden">All Leads</h4>
              <MobileLeadsCards refreshKey={leadsTick} />
            </div>
        
            {/* Desktop table */}
            <div className="lg:col-span-2 border-t pt-6 hidden md:block">
              <h4 className="text-sm font-semibold mb-3">All Leads</h4>
              <LeadsTable refreshKey={leadsTick} />
            </div>
          </div>
        </Card>
        
        )}


        {activeTab === "incoming" && (
          <Card title="Incoming Requests" right={<button onClick={() => location.reload()} className="text-sm px-3 py-1 border rounded hover:bg-gray-50">Refresh</button>}>
            <IncomingTasks api={API} safeDate={safeDate} findKpiMeta={findKpiMeta} />
          </Card>
        )}


        {activeTab === "funnel" && (
          <Card title="Funnel by Brand">
            <FunnelByBrand
              brands={brands}
              selectedBrandId={selectedBrandId}
              onBrandChange={setSelectedBrandId}
            />
          </Card>
        )}

      </div>
    </div>
  );
}
